import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Progress, Alert, Spin, message, Typography } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { ButtonItem } from "./Items";
import rutaPickingPdf from "../Pdf/rutaPickingPdf";
import VisorRutaPickingMapa from "./VisorRutaPickingMapa";
import TarjetaParadaPicking from "./TarjetaParadaPicking";
import PosicionRackButton from "./PosicionRackButton";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const { Text } = Typography;

//Ruta de picking de UN almacén (proyecto)
const RutaPickingAlmacen = ({ visible, empresaId, headersToken, proyectoId, proyectoNombre, insumosRequeridos, titulo, onRecolectadoChange }) => {
  const [cargando, setCargando] = useState(false);
  const [paradas, setParadas] = useState([]);
  const [faltantes, setFaltantes] = useState([]);
  const [sinUbicacionAsignada, setSinUbicacionAsignada] = useState([]);
  const [recolectado, setRecolectado] = useState({});
  const [mapaAlmacenId, setMapaAlmacenId] = useState(null);
  const [segmentosCamino, setSegmentosCamino] = useState([]);
  const [datosMapa, setDatosMapa] = useState(null);
  const [cargandoMapa, setCargandoMapa] = useState(false);

  const cargarRuta = useCallback(async () => {
    if (!proyectoId || !insumosRequeridos || insumosRequeridos.length === 0) {
      setParadas([]); setFaltantes([]); setSinUbicacionAsignada([]); setRecolectado({}); setMapaAlmacenId(null); setSegmentosCamino([]);
      return;
    }
    setCargando(true);
    try {
      const res = await axios.post("picking/generarRutaPicking", { empresaId, proyectoId, insumosRequeridos }, { headers: headersToken });
      if (res.data.success === 2) {
        const nuevasParadas = res.data.paradas || [];
        setParadas(nuevasParadas);
        setFaltantes(res.data.faltantes || []);
        setSinUbicacionAsignada(res.data.sinUbicacionAsignada || []);
        setMapaAlmacenId(res.data.mapaAlmacenId || null);
        setSegmentosCamino(res.data.segmentosCamino || []);
        const nuevoRecolectado = {};
        nuevasParadas.forEach((parada) => {
          (parada.insumos || []).forEach((insumo) => {
            nuevoRecolectado[`${parada.ubicacionId || parada.nodoAccesoId}_${insumo.insumoId}`] = { checked: false, cantidad: insumo.cantidad };
          });
        });
        setRecolectado(nuevoRecolectado);
      } else {
        message.error(res.data.error || "No se pudo generar la ruta de picking");
      }
    } catch (error) {
      console.error("Error al generar la ruta de picking:", error);
      message.error("No se pudo generar la ruta de picking");
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, headersToken, proyectoId, insumosRequeridos]);

  useEffect(() => {
    if (visible) cargarRuta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (!mapaAlmacenId) { setDatosMapa(null); return undefined; }
    let cancelado = false;
    setCargandoMapa(true);
    axios
      .post("mapaAlmacen/selectMapaAlmacen", { empresaId, proyectoId }, { headers: headersToken })
      .then((res) => { if (!cancelado && res.data.success === 2) setDatosMapa(res.data); })
      .catch((error) => console.error("Error al cargar el mapa para la ruta de picking:", error))
      .finally(() => { if (!cancelado) setCargandoMapa(false); });
    return () => { cancelado = true; };
  }, [empresaId, proyectoId, headersToken, mapaAlmacenId]);

  const cbRef = useRef(onRecolectadoChange);
  cbRef.current = onRecolectadoChange;
  useEffect(() => {
    const cantidades = {};
    Object.entries(recolectado).forEach(([llave, valor]) => {
      if (!valor.checked) return;
      const insumoId = llave.substring(llave.indexOf("_") + 1);
      cantidades[insumoId] = (cantidades[insumoId] || 0) + Number(valor.cantidad || 0);
    });
    if (cbRef.current) cbRef.current(cantidades);
  }, [recolectado]);

  const totalFilas = paradas.reduce((acc, parada) => acc + (parada.insumos || []).length, 0);
  const totalMarcadas = Object.values(recolectado).filter((v) => v.checked).length;

  const indiceParadaActual = useMemo(() => {
    for (let indice = 0; indice < paradas.length; indice++) {
      const parada = paradas[indice];
      const idParada = parada.ubicacionId || parada.nodoAccesoId;
      const todosMarcados = (parada.insumos || []).every((insumo) => recolectado[`${idParada}_${insumo.insumoId}`]?.checked);
      if (!todosMarcados) return indice;
    }
    return null;
  }, [paradas, recolectado]);

  const marcar = (llave, checked) => setRecolectado((prev) => ({ ...prev, [llave]: { ...prev[llave], checked } }));
  const cambiarCantidad = (llave, cantidad) => setRecolectado((prev) => ({ ...prev, [llave]: { ...prev[llave], cantidad } }));

  const exportarPdf = () => {
    const dd = rutaPickingPdf({ titulo, proyectoNombre, paradas, faltantes, sinUbicacionAsignada, datosMapa, segmentosCamino, indiceParadaActual });
    pdfMake.createPdf(dd).download(`RutaPicking_${(proyectoNombre || "almacen").replace(/\s+/g, "_")}.pdf`);
  };

  const renderAccionesInsumo = (insumo, idParada) => (
    <PosicionRackButton
      empresaId={empresaId}
      headersToken={headersToken}
      proyectoId={proyectoId}
      insumo={{ ...insumo, ubicacionId: insumo.ubicacionId || idParada }}
    />
  );

  return (
    <Spin spinning={cargando}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <ButtonItem rojo icon={<FilePdfOutlined />} size="small" onClick={exportarPdf} disabled={paradas.length === 0}>
          Imprimir esta ruta
        </ButtonItem>
      </div>
      {totalFilas > 0 && <Progress percent={Math.round((totalMarcadas / totalFilas) * 100)} style={{ marginBottom: 16 }} />}
      {sinUbicacionAsignada.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Hay existencia sin ubicación asignada; verifica físicamente"
          description={sinUbicacionAsignada.map((i) => `${i.clave} - ${i.descripcion}: ${i.cantidad}`).join(", ")} />
      )}
      {faltantes.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message="No hay existencia suficiente para algunos insumos"
          description={faltantes.map((i) => `${i.clave} - ${i.descripcion}: faltan ${i.cantidad}`).join(", ")} />
      )}
      <VisorRutaPickingMapa
        mapaAlmacenId={mapaAlmacenId}
        datosMapa={datosMapa}
        cargando={cargandoMapa}
        segmentosCamino={segmentosCamino}
        paradas={paradas}
        indiceParadaActual={indiceParadaActual}
        recolectado={recolectado}
        marcar={marcar}
        cambiarCantidad={cambiarCantidad}
        onImprimir={exportarPdf}
        accionesInsumo={renderAccionesInsumo}
      />
      {paradas.map((parada, indice) => (
        <TarjetaParadaPicking
          key={parada.ubicacionId || parada.nodoAccesoId}
          parada={parada}
          segmento={segmentosCamino[indice]}
          esActual={indice === indiceParadaActual}
          recolectado={recolectado}
          marcar={marcar}
          cambiarCantidad={cambiarCantidad}
          accionesInsumo={renderAccionesInsumo}
        />
      ))}
      {segmentosCamino.length > paradas.length && (
        <div style={{ fontSize: 11, color: "#8a93a6", marginBottom: 16 }}>
          Tramo final - {segmentosCamino[segmentosCamino.length - 1].distanciaMetros.toFixed(1)} m de la última parada hasta la salida
        </div>
      )}
      {!cargando && paradas.length === 0 && faltantes.length === 0 && sinUbicacionAsignada.length === 0 && (
        <Text type="secondary">No hay insumos pendientes para generar una ruta en este almacén.</Text>
      )}
    </Spin>
  );
};

export default RutaPickingAlmacen;
